'use client';

import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

const CasesSection = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const casesData = {
    ar: [
      {
        icon: 'ShoppingBagIcon',
        title: '1 — القضايا التجارية',
        description: 'نقدم الخدمات القانونية الخاصة بالقضايا التجارية، بما في ذلك النزاعات بين الشركات، والمطالبات المالية، ومخالفات الأنظمة التجارية.',
        link: '/commercial-and-bankruptcy-law-services-page'
      },
      {
        icon: 'DocumentTextIcon',
        title: '2 — صياغة العقود والاتفاقيات التجارية',
        description: 'صياغة ومراجعة العقود التجارية بكافة أنواعها بما يضمن حقوق الأطراف، ويحقق أعلى درجات الدقة القانونية.',
        link: '/contract-drafting-services-page'
      },
      {
        icon: 'CalculatorIcon',
        title: '3 — القضايا الضريبية',
        description: 'تمثيل العملاء في قضايا الزكاة والضريبة، وتقديم الاعتراضات، والمتابعة أمام الجهات المختصة.',
        link: '/tax-and-zakat-law-services-page'
      },
      {
        icon: 'BriefcaseIcon',
        title: '4 — قانون العمل والعمال',
        description: 'معالجة نزاعات العمل، وصياغة لوائح تنظيم العمل، وتمثيل العملاء أمام هيئات الفصل في الخلافات العمالية.',
        link: '/labor-and-employment-law-services-page'
      },
      {
        icon: 'GlobeAltIcon',
        title: '5 — الاستثمار الأجنبي',
        description: 'تأسيس الكيانات الأجنبية، وتسجيل الفروع، وتسهيل الإجراءات القانونية للمستثمرين داخل المملكة.',
        link: '/foreign-investment-services-page'
      },
      {
        icon: 'LightBulbIcon',
        title: '6 — الملكية الفكرية',
        description: 'تسجيل العلامات التجارية، وبراءات الاختراع، وحماية حقوق الملكية الفكرية أمام الجهات المختصة.',
        link: '/intellectual-property-services-page'
      },
      {
        icon: 'BanknotesIcon',
        title: '7 — النزاعات المصرفية والتمويلية',
        description: 'النظر في القضايا المتعلقة بالقروض، والعقود التمويلية، والنزاعات المصرفية مع الجهات المالية.',
        link: '/banking-and-financial-disputes-services-page'
      },
      {
        icon: 'ScaleIcon',
        title: '8 — التحكيم التجاري والوساطة والتسوية الودية',
        description: 'تمثيل العملاء في إجراءات التحكيم، وإدارة عمليات الوساطة، وتقديم الحلول الودية للنزاعات.',
        link: '/commercial-arbitration-and-mediation-services-page'
      },
      {
        icon: 'ExclamationTriangleIcon',
        title: '9 — دعاوى الإفلاس',
        description: 'تقديم المشورة القانونية في قضايا الإفلاس وإعادة التنظيم المالي، وتقديم الطلبات الرسمية.',
        link: '/bankruptcy-law-services-detail-page'
      },
      {
        icon: 'BuildingOffice2Icon',
        title: '10 — نظام الشركات',
        description: 'تأسيس الشركات، وتعديل عقود التأسيس، وتنظيم هياكل الملكية، وتقديم الاستشارات المتعلقة بالحَوْكمة.',
        link: '/corporate-law-services-detail-page'
      }
    ],
    en: [
      {
        icon: 'ShoppingBagIcon',
        title: '1 — Commercial Cases',
        description: 'We provide legal services for commercial cases, including disputes between companies, financial claims, and commercial regulation violations.',
        link: '/commercial-and-bankruptcy-law-services-page'
      },
      {
        icon: 'DocumentTextIcon',
        title: '2 — Drafting Commercial Contracts and Agreements',
        description: 'Drafting and reviewing commercial contracts of all types to ensure the rights of parties and achieve the highest levels of legal accuracy.',
        link: '/contract-drafting-services-page'
      },
      {
        icon: 'CalculatorIcon',
        title: '3 — Tax Cases',
        description: 'Representing clients in zakat and tax cases, filing objections, and following up with the relevant authorities.',
        link: '/tax-and-zakat-law-services-page'
      },
      {
        icon: 'BriefcaseIcon',
        title: '4 — Labor and Workers Law',
        description: 'Handling labor disputes, drafting employment regulations, and representing clients before labor dispute resolution bodies.',
        link: '/labor-and-employment-law-services-page'
      },
      {
        icon: 'GlobeAltIcon',
        title: '5 — Foreign Investment',
        description: 'Establishing foreign entities, registering branches, and facilitating legal procedures for investors within the Kingdom.',
        link: '/foreign-investment-services-page'
      },
      {
        icon: 'LightBulbIcon',
        title: '6 — Intellectual Property',
        description: 'Registering trademarks, patents, and protecting intellectual property rights before the relevant authorities.',
        link: '/intellectual-property-services-page'
      },
      {
        icon: 'BanknotesIcon',
        title: '7 — Banking and Financial Disputes',
        description: 'Handling cases related to loans, financing contracts, and banking disputes with financial institutions.',
        link: '/banking-and-financial-disputes-services-page'
      },
      {
        icon: 'ScaleIcon',
        title: '8 — Commercial Arbitration, Mediation and Amicable Settlement',
        description: 'Representing clients in arbitration proceedings, managing mediation processes, and providing amicable solutions to disputes.',
        link: null
      },
      {
        icon: 'ExclamationTriangleIcon',
        title: '9 — Bankruptcy Claims',
        description: 'Providing legal advice in bankruptcy and financial restructuring cases, and submitting official applications.',
        link: null
      },
      {
        icon: 'BuildingOffice2Icon',
        title: '10 — Companies Law',
        description: 'Establishing companies, amending articles of association, organizing ownership structures, and providing governance-related consultations.',
        link: null
      }
    ]
  };

  const items = casesData[language];
  const sectionTitle = language === 'ar' ? 'القضايا' : 'Cases';

  return (
    <section data-component-id="src/app/landing-page/components/CasesSection.tsx:141:4" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="141" data-component-end-line="190" data-component-file="CasesSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22cases%22%2C%22className%22%3A%22py-16%20bg-surface%20relative%20overflow-hidden%22%7D"
      id="cases"
      className="py-16 bg-surface relative overflow-hidden"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div data-component-id="src/app/landing-page/components/CasesSection.tsx:146:6" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="146" data-component-end-line="189" data-component-file="CasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20lg%3Apx-10%22%7D" className="container mx-auto px-4 lg:px-10">
        {/* Section Header */}
        <div data-component-id="src/app/landing-page/components/CasesSection.tsx:148:8" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="148" data-component-end-line="152" data-component-file="CasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h2 data-component-id="src/app/landing-page/components/CasesSection.tsx:149:10" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="149" data-component-end-line="151" data-component-file="CasesSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-3xl%20md%3Atext-4xl%20text-primary%20mb-4%22%7D" className="font-playfair font-bold text-3xl md:text-4xl text-primary mb-4">
            {sectionTitle}
          </h2>
        </div>

        {/* Cases Grid */}
        <div data-component-id="src/app/landing-page/components/CasesSection.tsx:155:8" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="155" data-component-end-line="188" data-component-file="CasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%20max-w-6xl%20mx-auto%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {items.map((item, index) => {
            const CardWrapper = item.link ? 'a' : 'div';
            const cardProps = item.link ? { href: item.link } : {};
            
            return (
              <CardWrapper data-component-id="src/app/landing-page/components/CasesSection.tsx:161:14" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="161" data-component-end-line="185" data-component-file="CasesSection.tsx" data-component-name="CardWrapper" data-component-content="%7B%22elementName%22%3A%22CardWrapper%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22className%22%3A%22bg-card%20rounded-xl%20p-6%20shadow-card%20hover%3Ashadow-xl%20transition-all%20duration-300%20border%20border-border%20hover%3Aborder-accent%20hover%3Ascale-105%20cursor-pointer%22%7D"
                key={index}
                {...cardProps}
                className="bg-card rounded-xl p-6 shadow-card hover:shadow-xl transition-all duration-300 border border-border hover:border-accent hover:scale-105 cursor-pointer"
              >
                {/* Icon */}
                <div data-component-id="src/app/landing-page/components/CasesSection.tsx:167:16" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="167" data-component-end-line="174" data-component-file="CasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-14%20h-14%20bg-accent%2F10%20rounded-xl%20flex%20items-center%20justify-center%20mb-4%20mx-auto%22%7D" className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Icon data-component-id="src/app/landing-page/components/CasesSection.tsx:168:18" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="168" data-component-end-line="173" data-component-file="CasesSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-accent%22%7D"
                    name={item.icon as any}
                    size={28}
                    variant="solid"
                    className="text-accent"
                  />
                </div>

                {/* Title */}
                <h3 data-component-id="src/app/landing-page/components/CasesSection.tsx:177:16" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="177" data-component-end-line="179" data-component-file="CasesSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-playfair%20font-semibold%20text-lg%20text-primary%20mb-2%20text-center%22%7D" className="font-playfair font-semibold text-lg text-primary mb-2 text-center">
                  {item.title}
                </h3>

                {/* Description */}
                <p data-component-id="src/app/landing-page/components/CasesSection.tsx:182:16" data-component-path="src/app/landing-page/components/CasesSection.tsx" data-component-line="182" data-component-end-line="184" data-component-file="CasesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-inter%20text-sm%20text-text-secondary%20leading-relaxed%20text-center%22%7D" className="font-inter text-sm text-text-secondary leading-relaxed text-center">
                  {item.description}
                </p>
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CasesSection;